import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster, toast } from 'react-hot-toast';
import * as Fa6Icons from 'react-icons/fa6';
import Header from './components/Header';
import Footer from './components/Footer';
import Converter from './components/Converter';
import MultiLangReference from './components/MultiLangReference';
import HelpModal from './components/HelpModal';
import FAQSection from './components/FAQSection';
import AboutSection from './components/AboutSection';
import SEO from './components/SEO';
import SafeIcon from './common/SafeIcon';

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [amount, setAmount] = useState('');
  const [selectedLang, setSelectedLang] = useState('zh_HK');
  const [showHelp, setShowHelp] = useState(false);
  const {
    FaFileInvoiceDollar,
    FaFileSignature,
    FaGlobe,
    FaShieldHalved,
    FaLock
  } = Fa6Icons;

  useEffect(() => {
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      setDarkMode(true);
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  const handleCopy = (text) => {
    if (!text || text === '等待轉換...') return;

    navigator.clipboard.writeText(text);
    toast.success('已複製到剪貼板', {
      style: {
        borderRadius: '12px',
        background: darkMode ? '#262626' : '#fff',
        color: darkMode ? '#fff' : '#262626',
        border: darkMode ? '1px solid #404040' : '1px solid #e5e5e5'
      }
    });
  };

  const infoCards = [
    {
      icon: FaFileInvoiceDollar,
      iconClass: 'bg-primary-50 dark:bg-primary-900/30 text-primary-500',
      borderClass:
        'hover:border-primary-200 dark:hover:border-primary-800',
      title: '專業支票填寫',
      description:
        '確保金額大寫與數字完全一致是財務安全的第一步。本工具自動處理各幣別的單位銜接（如港幣「圓正」或美金「Only」），並在空位補齊規則上符合主流銀行清算要求，有效防止惡意篡改。'
    },
    {
      icon: FaFileSignature,
      iconClass:
        'bg-secondary-50 dark:bg-secondary-900/30 text-secondary-500',
      borderClass:
        'hover:border-secondary-200 dark:hover:border-secondary-800',
      title: '法律文件合規',
      description:
        '在法律合同與審計中，大寫文字具有最高法律優先級。我們提供標準化的金融格式，廣泛應用於合約簽署、發票開立、借貸協議等具法律效力的正式文書，減少商務糾紛隱患。'
    },
    {
      icon: FaGlobe,
      iconClass: 'bg-accent-50 dark:bg-accent-900/30 text-accent-500',
      borderClass:
        'hover:border-accent-200 dark:hover:border-accent-800',
      title: '跨國商務結算',
      description:
        '支援英、法、德、意、泰、阿等 10+ 語言。特別針對歐洲「逗號小數點」與中東「從右至左」書寫習慣進行優化，助力全球貿易企業在跨境匯款與支付中實現高效、精準的結算。'
    }
  ];

  return (
    <HelmetProvider>
      <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-neutral-900 transition-colors duration-300 relative overflow-hidden">
        <SEO />
        <Toaster position="top-right" />

        <div className="fixed top-0 left-0 w-[500px] h-[500px] bg-primary-400/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="fixed bottom-0 right-0 w-[500px] h-[500px] bg-secondary-400/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

        <Header
          darkMode={darkMode}
          toggleDarkMode={() => setDarkMode((current) => !current)}
          openHelp={() => setShowHelp(true)}
        />

        <main
          className="container mx-auto px-4 py-8 md:py-12 max-w-5xl flex-grow z-10 relative"
          itemScope
          itemType="https://schema.org/WebApplication"
        >
          <p className="mb-4 text-right text-xs text-gray-500 dark:text-gray-400">
            網站內容最近更新：
            <time itemProp="dateModified" dateTime="2026-03-07">
              2026 年 3 月 7 日
            </time>
          </p>

          <Converter
            amount={amount}
            setAmount={setAmount}
            selectedLang={selectedLang}
            setSelectedLang={setSelectedLang}
            onCopy={handleCopy}
          />

          <MultiLangReference amount={amount} onCopy={handleCopy} />
          <FAQSection />

          <section
            aria-labelledby="use-cases-title"
            className="glass-card rounded-2xl shadow-xl p-8 border border-white/40 dark:border-neutral-700/50"
            itemScope
            itemType="https://schema.org/Article"
          >
            <meta itemProp="dateModified" content="2026-03-07" />
            <div className="mb-7">
              <h2
                id="use-cases-title"
                className="text-xl font-bold text-gray-900 dark:text-white"
                itemProp="headline"
              >
                Cheque Fill 適合用在哪些財務場景?
              </h2>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                使用情境與專業資訊 · 最近更新：
                <time itemProp="dateModified" dateTime="2026-03-07">
                  2026 年 3 月 7 日
                </time>
              </p>
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              itemProp="articleBody"
            >
              {infoCards.map((card) => (
                <article
                  key={card.title}
                  className={`group p-6 rounded-2xl bg-white/50 dark:bg-neutral-800/40 border border-gray-100 dark:border-neutral-700/30 hover:shadow-xl transition-all duration-300 ${card.borderClass}`}
                >
                  <div
                    className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-5 group-hover:scale-110 transition-transform ${card.iconClass}`}
                  >
                    <SafeIcon icon={card.icon} className="text-3xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">
                    {card.description}
                  </p>
                </article>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-100 dark:border-neutral-700/50">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 px-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-600">
                    <SafeIcon icon={FaLock} className="text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                      輸入的金額資料會被儲存嗎?
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      所有轉換均在本地瀏覽器完成，我們不會記錄或傳輸任何金額數據。
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600">
                    <SafeIcon icon={FaShieldHalved} className="text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                      轉換結果可以直接作法律用途嗎?
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      轉換結果僅供參考，實際填寫前請務必核對銀行及當地法律具體要求。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <AboutSection />
        </main>

        <Footer />
        <HelpModal
          isOpen={showHelp}
          onClose={() => setShowHelp(false)}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;